/**
 * Field ID Mapping
 * 
 * Maps between unified field IDs (used by dropdown) and internal IDs (used by views).
 * Some views use different internal field IDs for historical reasons.
 */

const FIELD_ID_MAP = {
  flow: {
    status: 'statusDropdown',
    priority: 'priorityDropdown',
    difficulty: 'difficultyDropdown',
    tags: 'categoryDropdown',
    conditions: 'conditionsIndicator',
    icon: 'missionIcon',
    attachments: 'attachmentsButton',
    estimatedDurationMinutes: 'estimatedDuration',
  },
  kanban: {
    status: 'statusDropdown',
    priority: 'priorityDropdown',
    difficulty: 'difficultyDropdown',
    tags: 'categoryDropdown',
    conditions: 'conditionsIndicator',
    icon: 'missionIcon',
    attachments: 'attachmentsButton',
    estimatedDurationMinutes: 'estimatedDuration',
  },
  neural: {
    status: 'statusDropdown',
    priority: 'priorityDropdown',
    difficulty: 'difficultyDropdown',
    tags: 'categoryDropdown',
    conditions: 'conditionsIndicator',
    icon: 'missionIcon',
    attachments: 'attachmentsButton',
    estimatedDurationMinutes: 'estimatedDuration',
  },
  table: {
    // table uses unified IDs directly
  },
  todolist: {
    conditions: 'dependencies',
  },
};

/**
 * Get the internal field ID for a view
 */
export const getInternalFieldId = (unifiedId, view) => {
  const viewMap = FIELD_ID_MAP[view];
  return viewMap?.[unifiedId] ?? unifiedId;
};

/**
 * Get the unified field ID from an internal ID
 */
export const getUnifiedFieldId = (internalId, view) => {
  const viewMap = FIELD_ID_MAP[view];
  if (!viewMap) return internalId;
  
  for (const [unified, internal] of Object.entries(viewMap)) {
    if (internal === internalId) return unified;
  }
  return internalId;
};

/**
 * Convert visibility settings from internal IDs to unified IDs
 */
export const toUnifiedVisibility = (internalVisibility, view) => {
  const viewMap = FIELD_ID_MAP[view];
  if (!viewMap) return internalVisibility;
  
  const unified = { ...internalVisibility };
  
  for (const [unifiedId, internalId] of Object.entries(viewMap)) {
    if (internalId in unified) {
      // If both keys exist (legacy + current), keep the field on if either is on.
      unified[unifiedId] = unifiedId in unified
        ? unified[unifiedId] === true || unified[internalId] === true
        : unified[internalId];
      delete unified[internalId];
    }
  }
  
  return unified;
};

/**
 * Convert visibility settings from unified IDs to internal IDs
 */
export const toInternalVisibility = (unifiedVisibility, view) => {
  const viewMap = FIELD_ID_MAP[view];
  if (!viewMap) return unifiedVisibility;
  
  const internal = { ...unifiedVisibility };
  
  for (const [unifiedId, internalId] of Object.entries(viewMap)) {
    if (unifiedId in internal) {
      internal[internalId] = internal[unifiedId];
      delete internal[unifiedId];
    }
  }
  
  return internal;
};
